/**
 * Staff Roles Configuration
 *
 * Defines roles for installers (specialists) and dealers,
 * plus mapping from free-text roles to standard roles.
 *
 * NEW: Unified role system (user_companies.role)
 * - admin: Full access including company/group management
 * - manager: Can view data and take actions (settle/void)
 * - technician: Can view data only
 * - viewer: Can view data only
 */

// NEW: Unified roles for all user types (stored in user_companies.role)
export const UNIFIED_ROLES = [
  { value: 'admin', label: 'Admin', description: 'Full access including company/group management' },
  { value: 'manager', label: 'Manager', description: 'Can view data and take actions (settle/void)' },
  { value: 'technician', label: 'Technician', description: 'Can view data only' },
  { value: 'viewer', label: 'Viewer', description: 'Can view data only' }
];

// NEW: Get badge color for unified role
export function getUnifiedRoleBadgeColor(role) {
  switch (role) {
    case 'admin': return 'danger';
    case 'manager': return 'primary';
    case 'technician': return 'success';
    case 'viewer': return 'secondary';
    default: return 'secondary';
  }
}

// NEW: Company type definitions
export const COMPANY_TYPES = {
  DEALER: 'dealer',
  DEALER_GROUP: 'dealer_group',
  INSTALLER: 'installer',
  INSTALLER_LOCATION: 'installer_location'
};

// NEW: Assignment type options for dealers
export const DEALER_ASSIGNMENT_TYPES = [
  { value: 'dealer', label: 'Single Dealership', description: 'Access to one specific dealership' },
  { value: 'dealer_group', label: 'Dealer Group', description: 'Access to all dealerships in a group (or scoped to one)' }
];

// NEW: Assignment type options for installers
export const INSTALLER_ASSIGNMENT_TYPES = [
  { value: 'installer_location', label: 'Single Location', description: 'Access to one specific location' },
  { value: 'installer', label: 'Installer Company', description: 'Access to all locations in company (or scoped to one)' }
];

// LEGACY: Installer (Specialist) roles - kept for backward compatibility
export const INSTALLER_ROLES = [
  { value: 'technician', label: 'Technician', description: 'Sees only their assigned work in the "My Work" view' },
  { value: 'manager', label: 'Manager', description: 'Can assign work, manage jobs, and view all shop activity' },
  { value: 'owner', label: 'Owner/Operator', description: 'Full access including staff management and settings' }
];

// LEGACY: Dealer roles - kept for backward compatibility
export const DEALER_ROLES = [
  { value: 'sales', label: 'Sales', description: 'Sales team member - receives notifications for their deals' },
  { value: 'finance', label: 'Finance', description: 'Finance team member - handles payment and documentation' },
  { value: 'manager', label: 'Manager', description: 'Full access to all dealership activity and staff management' }
];

/**
 * Map common free-text role values to standard dealer roles
 * Used when displaying/editing contacts that were created with free-text roles
 */
const DEALER_ROLE_MAPPINGS = {
  // Sales variations
  'sales': 'sales',
  'salesperson': 'sales',
  'sales person': 'sales',
  'salesman': 'sales',
  'saleswoman': 'sales',
  'sales rep': 'sales',
  'sales representative': 'sales',
  'sales associate': 'sales',
  'sales consultant': 'sales',
  'sales advisor': 'sales',
  'internet sales': 'sales',
  'bdc': 'sales',
  'bdc rep': 'sales',
  'bdc agent': 'sales',

  // Finance variations
  'finance': 'finance',
  'finance manager': 'finance',
  'f&i': 'finance',
  'f&i manager': 'finance',
  'finance and insurance': 'finance',
  'business manager': 'finance',
  'deal closer': 'finance',

  // Manager variations
  'manager': 'manager',
  'general manager': 'manager',
  'gm': 'manager',
  'sales manager': 'manager',
  'desk manager': 'manager',
  'floor manager': 'manager',
  'owner': 'manager',
  'dealer': 'manager',
  'principal': 'manager',
  'director': 'manager',

  // Service variations -> map to manager for now (they have broader access)
  'service advisor': 'manager',
  'service writer': 'manager',
  'service manager': 'manager',
  'parts manager': 'manager',
  'fixed ops': 'manager',
  'fixed operations': 'manager'
};

/**
 * Map a free-text role to a standard dealer role
 * @param {string} freeTextRole - The role string from the database
 * @returns {string|null} - The standard role value, or null if no mapping found
 */
export function mapToDealerRole(freeTextRole) {
  if (!freeTextRole) return null;

  const normalized = freeTextRole.toLowerCase().trim();
  return DEALER_ROLE_MAPPINGS[normalized] || null;
}

/**
 * Get the display label for a role
 * @param {string} roleValue - The role value (e.g., 'sales', 'technician')
 * @param {string} userType - 'installer' or 'dealer'
 * @returns {string} - The display label
 */
export function getRoleLabel(roleValue, userType) {
  const roles = userType === 'dealer' ? DEALER_ROLES : INSTALLER_ROLES;
  const role = roles.find(r => r.value === roleValue);
  return role ? role.label : roleValue || 'Staff';
}

/**
 * Get roles array for a user type
 * @param {string} userType - 'installer' or 'dealer'
 * @returns {Array} - Array of role objects
 */
export function getRolesForUserType(userType) {
  return userType === 'dealer' ? DEALER_ROLES : INSTALLER_ROLES;
}

/**
 * Check if a role value is valid for a user type
 * @param {string} roleValue - The role to check
 * @param {string} userType - 'installer' or 'dealer'
 * @returns {boolean}
 */
export function isValidRole(roleValue, userType) {
  const roles = getRolesForUserType(userType);
  return roles.some(r => r.value === roleValue);
}

/**
 * NEW: Check if a role is a valid unified role
 * @param {string} roleValue - The role to check
 * @returns {boolean}
 */
export function isValidUnifiedRole(roleValue) {
  return UNIFIED_ROLES.some(r => r.value === roleValue);
}

/**
 * NEW: Map legacy role to unified role
 * @param {string} legacyRole - The old role value
 * @param {string} userType - 'installer' or 'dealer'
 * @returns {string} - The unified role value
 */
export function mapLegacyToUnifiedRole(legacyRole, userType) {
  if (!legacyRole) return 'viewer';

  const normalized = legacyRole.toLowerCase().trim();

  // If already a unified role, return as-is
  if (isValidUnifiedRole(normalized)) {
    return normalized;
  }

  // Map installer legacy roles
  if (userType === 'installer') {
    switch (normalized) {
      case 'owner': return 'admin';
      case 'manager': return 'manager';
      case 'technician': return 'technician';
      default: return 'viewer';
    }
  }

  // Map dealer legacy roles
  if (userType === 'dealer') {
    switch (normalized) {
      case 'manager': return 'admin';
      case 'finance': return 'manager';
      case 'sales': return 'technician';
      default: return 'viewer';
    }
  }

  return 'viewer';
}

/**
 * NEW: Get unified role label
 * @param {string} roleValue - The unified role value
 * @returns {string} - The display label
 */
export function getUnifiedRoleLabel(roleValue) {
  const role = UNIFIED_ROLES.find(r => r.value === roleValue);
  return role ? role.label : roleValue || 'Viewer';
}
